import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Radio,
  Table,
  Upload,
  message,
  Progress,
  Button,
  Avatar,
  Typography,
  Dropdown,
  Input,
  Modal,
  Spin,
  Tag,
  Tabs,
} from "antd";
import {
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

export default function CommercialDetails() {
  const { uuid } = useParams();
  const [commercials, setCommercials] = useState();
  const [loading, setLoading] = useState(true);
  const [commercialDetails, setCommercialDetails] = useState({});
  const [organisationLoading, setOrganisationLoading] = useState(false);

  const navigate = useNavigate();

  const fetchCommercials = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_COM_URL}/commercials/${uuid}`
      );

      if (!res.ok) {
        throw new Error("Erreur lors de la récupération des données");
      }

      const data = await res.json();
      setCommercials(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercialsOrg = async () => {
    try {
      setOrganisationLoading(true);
      const res = await fetch(`${process.env.REACT_APP_COM_URL}/commercials/`);

      if (!res.ok) {
        throw new Error("Erreur lors de la récupération des données");
      }

      const data = await res.json();
      const org = data.find((res) => res.uuid === uuid);
      setCommercialDetails(org || {});
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setOrganisationLoading(false);
    }
  };

  useEffect(() => {
    fetchCommercials();
    fetchCommercialsOrg();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Composant pour les informations générales
  const GeneralInfoTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#1890ff",
        }}
      >
        Informations générales
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
        }}
      >
        <div>
          <strong>Nom du commercial :</strong>
          <div style={{ marginTop: "5px" }}>
            {commercials?.fullname || "N/A"}
          </div>
        </div>
        <div>
          <strong>ID du commercial :</strong>
          <div
            style={{
              marginTop: "5px",
              fontFamily: "monospace",
              backgroundColor: "#f5f5f5",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            {commercials?.uuid || "N/A"}
          </div>
        </div>
        <div>
          <strong>Téléphone :</strong>
          <div style={{ marginTop: "5px" }}>+{commercials?.phone || "N/A"}</div>
        </div>
        <div>
          <strong>Statut :</strong>
          <div style={{ marginTop: "5px" }}>
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                backgroundColor:
                  commercials?.status === "active" ? "#f6ffed" : "#fff2f0",
                color: commercials?.status === "active" ? "#52c41a" : "#ff4d4f",
              }}
            >
              {commercials?.status === "active" ? "Actif" : "Inactif"}
            </span>
          </div>
        </div>
        <div>
          <strong>Date de création :</strong>
          <div style={{ marginTop: "5px" }}>
            {commercials?.createdAt
              ? new Date(commercials.createdAt).toLocaleString()
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );

  // Composant pour les organisations
  const OrganisationsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#722ed1",
        }}
      >
        Organisations ({commercialDetails.organisations?.length || 0})
      </h3>

      {organisationLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : commercialDetails.organisations?.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#fafafa" }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Nom
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Type de compte
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Statut
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Numéro de l'organisation
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  QR Code
                </th>
              </tr>
            </thead>
            <tbody>
              {commercialDetails.organisations?.map((org) => (
                <tr
                  key={org.organisationId}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td style={{ padding: "12px" }}>{org.name}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          org.accountType === "marchand"
                            ? "#e6f7ff"
                            : org.accountType === "school"
                            ? "#fff7e6"
                            : org.accountType === "pdv"
                            ? "#F0FFFF"
                            : org.accountType === "enterprise"
                            ? "#DDA0DD"
                            : "#f5f5f5",
                        color:
                          org.accountType === "marchand"
                            ? "#1890ff"
                            : org.accountType === "school"
                            ? "#fa8c16"
                            : org.accountType === "pdv"
                            ? "#20B2AA"
                            : org.accountType === "enterprise"
                            ? "#9932CC"
                            : "#666",
                      }}
                    >
                      {org.accountType}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          org.status === "active" ? "#f6ffed" : "#fff2f0",
                        color: org.status === "active" ? "#52c41a" : "#ff4d4f",
                      }}
                    >
                      {org.status === "active" ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: "#f6ffed",
                      }}
                    >
                      {org.phone}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <a
                      href={org.qrCode?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: "#f6ffed",
                        textDecoration: "none",
                        color: "#52c41a",
                      }}
                    >
                      {org.qrCode?.url || "N/A"}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune organisation trouvée
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Message d'alerte */}
      {message.text && (
        <div
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e8",
            color: message.type === "error" ? "#c62828" : "#2e7d32",
            border: `1px solid ${
              message.type === "error" ? "#ef5350" : "#66bb6a"
            }`,
          }}
        >
          {message.text}
        </div>
      )}

      {/* Header avec bouton retour */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "600" }}>
          Détails du commercial
        </h1>
        <button
          onClick={() => navigate("/commercials")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#f5f5f5",
            color: "#333",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          ← Retour à la liste
        </button>
      </div>

      {/* Tabs pour organiser le contenu */}
      <Tabs defaultActiveKey="1" type="card" size="large">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Informations générales
            </span>
          }
          key="1"
        >
          <GeneralInfoTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <BankOutlined />
              Organisations ({commercialDetails.organisations?.length || 0})
            </span>
          }
          key="2"
        >
          <OrganisationsTab />
        </TabPane>
      </Tabs>
    </div>
  );
}
